import { api } from "@/lib/api/client";
import type { paths } from "@/lib/api/generated/api";

/**
 * The session endpoints.
 *
 * Types come from the generated OpenAPI document, so a backend change to the payload is a
 * TypeScript error here rather than a runtime surprise three screens deep.
 */

type SessionResponse =
  paths["/api/v1/auth/session"]["get"]["responses"][200]["content"]["application/json"];

/** What the app knows about the person using it. */
export type Session = SessionResponse extends { data: infer T } ? T : never;

export interface LoginInput {
  email: string;
  password: string;
}

/**
 * What `POST /auth/login` answers (17 Ekim 2026, #31): `step: "SESSION"` — signed in; or
 * `MFA_VERIFY` / `MFA_ENROL` — NO session yet, the second step (`loginMfaApi`) follows.
 */
export type LoginResult =
  paths["/api/v1/auth/login"]["post"]["responses"][200]["content"]["application/json"] extends {
    data: infer T;
  }
    ? T
    : never;

/** The one-time invitation token plus the first password. */
export type AcceptInviteInput =
  paths["/api/v1/auth/accept-invite"]["post"]["requestBody"]["content"]["application/json"];

/** Parola sıfırlamanın iki ucu: adres, sonra jeton + yeni parola. */
export type RequestPasswordResetInput =
  paths["/api/v1/auth/password-reset/request"]["post"]["requestBody"]["content"]["application/json"];

export type CompletePasswordResetInput =
  paths["/api/v1/auth/password-reset/complete"]["post"]["requestBody"]["content"]["application/json"];

/**
 * What accepting an invitation answers with.
 *
 * Hand-written because the OpenAPI document declares no body for the 200; the shape is the
 * handler's `{ id, email, mfaRequired }`. Note what is NOT here: a session. The account is
 * activated, and the person still signs in.
 */
export interface AcceptInviteResult {
  id: string;
  email: string;
  mfaRequired: boolean;
}

export const sessionApi = {
  /**
   * Who am I and what may I do.
   *
   * A 401 here is not an error to report — it is the answer "nobody, yet", and the caller
   * turns it into the sign-in screen. That is why the query that wraps this does not retry it.
   */
  describe(): Promise<Session> {
    return api.get<Session>("/auth/session");
  },

  /**
   * Moves this session into another customer, and returns the session as it now is.
   *
   * THE WHOLE SESSION COMES BACK, not an acknowledgement. Permissions, reachable companies and
   * the enabled module list all belong to the customer, so half of what the app is holding goes
   * stale the instant the customer changes. Returning the new payload means the caller replaces
   * it in one step instead of reasoning about which half to refetch — and a screen never renders
   * one customer's menu over another customer's data.
   *
   * A customer the caller cannot reach answers 404, not 403: the same rule as every other
   * out-of-reach row here, so one they may not enter looks exactly like one that does not exist.
   */
  switchTenant(tenantId: string): Promise<Session> {
    return api.post<Session>("/auth/session/tenant", { tenantId });
  },
  login(input: LoginInput): Promise<LoginResult> {
    return api.post<LoginResult>("/auth/login", input);
  },

  /**
   * Sets the first password of an invited account and burns the token.
   *
   * Public and rate limited by IP on the server. A used, expired or unknown token answers
   * 401 `TOKEN_INVALID`; the caller renders that as a dead link, not as an expired session —
   * there was never a session here to expire.
   */
  acceptInvite(input: AcceptInviteInput): Promise<AcceptInviteResult> {
    return api.post<AcceptInviteResult>("/auth/accept-invite", input);
  },

  /**
   * «Parolamı unuttum» — ve cevabı HİÇBİR ŞEY SÖYLEMEZ.
   *
   * Sunucu adres var da yok da birebir aynı gövdeyi (`{accepted: true}`) döndürüyor; jeton
   * yalnız postayla gidiyor. Bu yüzden burada okunacak bir sonuç yok ve ekran her durumda
   * aynı «gönderildiyse gelecek» metnini gösteriyor — cevaba bakıp «bu adres kayıtlı» demek,
   * sunucunun tam olarak vermemek için uğraştığı bilgi olurdu
   * (`tests/unit/password-reset-public-response.test.ts`).
   */
  requestPasswordReset(input: RequestPasswordResetInput): Promise<unknown> {
    return api.post("/auth/password-reset/request", input);
  },

  /**
   * Jetonu yakar ve yeni parolayı yazar. OTURUM AÇMAZ — davet akışının aynısı: kişi yeni
   * parolasıyla giriş ekranına dönüyor.
   *
   * Kullanılmış, süresi dolmuş ya da bilinmeyen jeton 401 veriyor; çağıran bunu «bağlantı
   * ölü» diye çiziyor, «oturum süresi doldu» diye değil — bu sayfada hiç oturum yoktu.
   */
  completePasswordReset(input: CompletePasswordResetInput): Promise<unknown> {
    return api.post("/auth/password-reset/complete", input);
  },

  /**
   * Kişinin kendi parolası — eskisini bilerek (`POST /auth/password/change`). Öteki cihazlardaki oturumlar
   * kapanır, bu oturum sürer; `mustChangePassword` da burada iner (31 Ekim 2026: yöneticinin verdiği
   * parolayla ilk giriş bu ekrandan geçiyor).
   */
  changePassword(input: { currentPassword: string; newPassword: string }): Promise<unknown> {
    return api.post("/auth/password/change", input);
  },
  logout(): Promise<void> {
    return api.post<void>("/auth/logout");
  },

  /**
   * Step-up re-authentication for a critical action.
   *
   * Proves the person is still the account holder, and the proof is stamped on THIS session
   * for a configured window. The action that triggered it is retried by the caller afterwards
   * — this only removes the obstacle.
   */
  reauthenticate(password: string): Promise<unknown> {
    return api.post("/auth/reauthenticate", { password });
  },
};
